import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useMovementsGlobal, useExportMovementsCsv } from '../hooks';
import { MovementTypeBadge, MOVEMENT_TYPE_LABELS } from '../components/MovementTypeBadge';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { MovementType } from '../api';
import { formatarData, formatarOrigemDestino } from '../utils/formatters';

const TYPE_OPTIONS: Array<[MovementType, string]> = Object.entries(MOVEMENT_TYPE_LABELS) as Array<[MovementType, string]>;
const LIMIT = 25;
/** Abas da home de Ativos — únicas aceitas em `?origem=`, pro breadcrumb. */
const ORIGIN_TABS = new Set(['lista', 'inventarios', 'almoxarifado']);

export default function MovimentacoesPage() {
  const [searchParams] = useSearchParams();
  const [type, setType] = useState<MovementType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [performedByUserId, setPerformedByUserId] = useState('');
  const [assetIdFilter, setAssetIdFilter] = useState('');
  const [page, setPage] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const filters = {
    type: type || undefined,
    from: from || undefined,
    to: to || undefined,
    performedByUserId: performedByUserId || undefined,
    assetId: assetIdFilter || undefined,
  };

  const query = useMovementsGlobal({ ...filters, limit: LIMIT, offset: page * LIMIT });
  const exportMutation = useExportMovementsCsv();

  const total = query.data?.pagination.count ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / LIMIT) - 1);
  const items = query.data?.data ?? [];

  const originTab = searchParams.get('origem');
  const backTo = originTab && ORIGIN_TABS.has(originTab) ? `/ativos?tab=${originTab}` : '/ativos';

  async function handleExport() {
    setExportError(null); setExportNotice(null);
    try {
      const { blob, truncated, rowCount } = await exportMutation.mutateAsync(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `movimentacoes-ativos-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (truncated) {
        setExportNotice(
          `Atenção — o CSV foi truncado em ${(rowCount ?? 10000).toLocaleString('pt-BR')} linhas. Refine os filtros e exporte novamente para não perder dados.`,
        );
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setExportError(typeof msg === 'string' ? msg : 'Falha ao exportar CSV');
    }
  }

  return (
    <PageContainer
      title="Movimentações de ativos"
      breadcrumbs={[{ label: 'Ativos', to: backTo }, { label: 'Movimentações' }]}
      action={
        <Button
          variant="secondary"
          icon={<Download className="h-4 w-4" />}
          loading={exportMutation.isPending}
          onClick={handleExport}
          className="!border-green-300 !text-green-700 hover:!bg-green-50"
        >
          {exportMutation.isPending ? 'Exportando…' : 'Exportar CSV'}
        </Button>
      }
    >
      {exportError && <Alert variant="error">{exportError}</Alert>}
      {exportNotice && (
        <Alert
          variant="warning"
          action={<Button variant="ghost" size="sm" onClick={() => setExportNotice(null)}>Fechar</Button>}
        >
          {exportNotice}
        </Alert>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <AutocompleteSelect
            label="Tipo"
            options={[
              { value: '', label: 'Todos os tipos' },
              ...TYPE_OPTIONS.map(([v, l]) => ({ value: v, label: l })),
            ]}
            value={type}
            onChange={(v) => { setType(v as MovementType | ''); setPage(0); }}
          />
          <TextInput
            label="De"
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(0); }}
          />
          <TextInput
            label="Até"
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(0); }}
          />
          <TextInput
            label="UUID do executor"
            type="text"
            value={performedByUserId}
            onChange={(e) => { setPerformedByUserId(e.target.value); setPage(0); }}
            className="font-mono"
          />
          <TextInput
            label="UUID do ativo"
            type="text"
            value={assetIdFilter}
            onChange={(e) => { setAssetIdFilter(e.target.value); setPage(0); }}
            className="font-mono"
          />
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Data</th>
                <th className="px-4 py-2 text-left">Tipo</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-left">Responsável (origem → destino)</th>
                <th className="px-4 py-2 text-left">Localização (origem → destino)</th>
                <th className="px-4 py-2 text-left">Executor</th>
                <th className="px-4 py-2 text-left">Ativo (id)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {query.isLoading && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-gray-500">Carregando...</td></tr>
              )}
              {query.isError && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-red-600">Falha ao carregar movimentações.</td></tr>
              )}
              {!query.isLoading && !query.isError && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-0">
                    <EmptyState
                      title="Nenhum movimento encontrado"
                      description="Ajuste os filtros ou aguarde novas movimentações."
                      size="compact"
                    />
                  </td>
                </tr>
              )}
              {items.map((m) => {
                const dt = new Date(m.occurredAt);
                return (
                  <tr key={m.id}>
                    <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
                      {formatarData(m.occurredAt)}
                      <span className="ml-1 text-xs text-gray-500">
                        {dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td className="px-4 py-2"><MovementTypeBadge type={m.type} /></td>
                    <td className="px-4 py-2 text-gray-700">{m.description}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {formatarOrigemDestino(m.previousCustodianName, m.newCustodianName)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {formatarOrigemDestino(m.previousLocationName, m.newLocationName ?? m.locationName)}
                    </td>
                    <td className="px-4 py-2 text-gray-700">
                      {m.performedByUserName ?? m.performedByUserEmail ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <Link to={`/ativos/${m.assetId}`} className="font-mono text-blue-600 hover:underline">
                        {m.assetId.slice(0, 8)}...
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2 text-sm">
          <span className="text-gray-500">{total} movimento{total === 1 ? '' : 's'}</span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <span>Página {page + 1} de {Math.max(1, maxPage + 1)}</span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= maxPage}
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
            >
              Próxima
            </Button>
          </div>
        </div>
      </section>
    </PageContainer>
  );
}
